/**
 * StatusBar Component
 *
 * Shows current state and quick metrics at the bottom of the screen.
 */

import React from 'react';
import { useStore } from '../store';
import { useWitsPrices } from '../hooks/useApi';
import DataBadge from './DataBadge';

const StatusBar: React.FC = () => {
  const { selectedCaseId, cases, isConnected, isSolving, solverResult, gridSummary, solverConfig } = useStore();
  const { data: wits, isLoading: witsLoading, error: witsError } = useWitsPrices(['BEN2201']);

  const selectedCase = cases.find((c) => c.id === selectedCaseId);

  return (
    <footer className="flex items-center justify-between px-4 py-1.5 bg-gray-800 border-t border-gray-700 text-xs">
      {/* Left section */}
      <div className="flex items-center space-x-4">
        <span className="text-gray-400">
          Load factor: <span className="text-gray-200 font-medium">{solverConfig.load_factor.toFixed(2)}x</span>
        </span>
        <span className="text-gray-400">
          {selectedCase ? (
            <>
              Case: <span className="text-white">{selectedCase.name}</span>
            </>
          ) : (
            'No case selected'
          )}
        </span>

        {selectedCase && (
          <>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400">
              Buses: <span className="text-white">{selectedCase.bus_count}</span>
            </span>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400">
              Branches: <span className="text-white">{selectedCase.branch_count}</span>
            </span>
          </>
        )}

        {/* Grid summary from API */}
        {gridSummary && (
          <>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400">
              Cap: <span className="text-green-400">{gridSummary.total_capacity_mw.toLocaleString()} MW</span>
              <DataBadge tier="MODELLED" label="MODELLED" className="ml-2" showDot={false} />
            </span>
          </>
        )}
      </div>

      {/* Center section */}
      <div className="flex items-center space-x-4">
        {isSolving && (
          <span className="flex items-center text-yellow-400">
            <span className="animate-pulse mr-1">●</span>
            Solving...
          </span>
        )}

        {/* Solver result summary */}
        {solverResult && !isSolving && (
          <span className={`flex items-center ${solverResult.converged ? 'text-green-400' : 'text-red-400'}`}>
            <span className="mr-1">{solverResult.converged ? '✓' : '✗'}</span>
            {solverResult.converged ? 'Converged' : 'Failed'}
            <DataBadge tier={solverResult.provenance?.tier || 'MODELLED'} label="MODEL" className="ml-2" showDot={false} />
            <span className="text-gray-400 ml-2">
              V: {solverResult.summary.min_voltage_pu.toFixed(3)}-{solverResult.summary.max_voltage_pu.toFixed(3)} pu
            </span>
            <span className="text-gray-400 ml-2">
              Loss: {solverResult.summary.total_losses_mw.toFixed(1)} MW
            </span>
          </span>
        )}
      </div>

      {/* Right section */}
      <div className="flex items-center space-x-4">
        <span className="text-gray-400 flex items-center gap-2">
          WITS:
          {witsLoading ? (
            <span className="text-gray-500">Loading</span>
          ) : witsError ? (
            <span className="text-red-400">Unavailable</span>
          ) : wits?.prices?.length ? (
            <DataBadge tier="LIVE" label="LIVE" showDot={false} />
          ) : (
            <span className="text-gray-500">No data</span>
          )}
        </span>
        <span className="text-gray-600">|</span>
        <span className="text-gray-400">
          API:{' '}
          <span className={isConnected ? 'text-green-400' : 'text-red-400'}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </span>
        <span className="text-gray-600">|</span>
        <span className="text-gray-500">GridTwinX v1.0.0</span>
      </div>
    </footer>
  );
};

export default StatusBar;
